/**
 * Firebase Service - Authentication and Premium Status
 */

import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore'
import { auth, db } from '../config/firebase'

/**
 * Check if user has premium subscription
 */
export async function checkPremiumStatus(userId) {
  if (!userId) return false
  
  try {
    const userDoc = await getDoc(doc(db, 'users', userId))
    if (!userDoc.exists()) {
      return false
    }
    
    const userData = userDoc.data()
    const hasPremium = userData.premium === true
    const premiumExpiry = userData.premiumExpiry
    
    // Check if premium has expired
    if (hasPremium && premiumExpiry) {
      const now = new Date()
      const expiry = premiumExpiry.toDate()
      if (now > expiry) {
        // Premium expired - update status
        await updateDoc(doc(db, 'users', userId), {
          premium: false,
          premiumExpiry: null
        })
        return false
      }
    }
    
    return hasPremium
  } catch (error) {
    console.error('Error checking premium status:', error)
    return false
  }
}

/**
 * Set premium status for user (called by webhook)
 */
export async function setPremiumStatus(userId, isPremium, expiryDate = null) {
  if (!userId) return false
  
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      await updateDoc(userRef, {
        premium: isPremium,
        premiumExpiry: expiryDate ? expiryDate : null,
        updatedAt: serverTimestamp()
      })
    } else {
      await setDoc(userRef, {
        premium: isPremium,
        premiumExpiry: expiryDate ? expiryDate : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    
    return true
  } catch (error) {
    console.error('Error setting premium status:', error)
    return false
  }
}

/**
 * Sign up with email and password
 */
export async function signUp(email, password, source = 'parkort') {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user
    
    // Get existing user doc if it exists (from other projects)
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    
    if (userDoc.exists()) {
      // User exists - update with new source if not already tracked
      const existingData = userDoc.data()
      const sources = existingData.sources || []
      if (!sources.includes(source)) {
        sources.push(source)
      }
      
      await updateDoc(doc(db, 'users', user.uid), {
        sources: sources,
        lastSource: source,
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } else {
      // New user - create document with source tracking
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        premium: false,
        sources: [source],
        lastSource: source,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    
    return { success: true, user }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email, password, source = 'parkort') {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    const user = userCredential.user
    
    // Update user document with source tracking
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    
    if (userDoc.exists()) {
      const existingData = userDoc.data()
      const sources = existingData.sources || []
      if (!sources.includes(source)) {
        sources.push(source)
      }
      
      await updateDoc(doc(db, 'users', user.uid), {
        sources: sources,
        lastSource: source,
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    
    return { success: true, user: userCredential.user }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle(source = 'parkort') {
  try {
    const provider = new GoogleAuthProvider()
    const userCredential = await signInWithPopup(auth, provider)
    const user = userCredential.user
    
    // Check if user document exists
    const userDoc = await getDoc(doc(db, 'users', user.uid))
    
    if (userDoc.exists()) {
      // User exists - update with new source if not already tracked
      const existingData = userDoc.data()
      const sources = existingData.sources || []
      if (!sources.includes(source)) {
        sources.push(source)
      }
      
      await updateDoc(doc(db, 'users', user.uid), {
        sources: sources,
        lastSource: source,
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    } else {
      // New user - create document with source tracking
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        premium: false,
        sources: [source],
        lastSource: source,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }
    
    return { success: true, user }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * Sign out
 */
export async function signOutUser() {
  try {
    await signOut(auth)
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

/**
 * Get current user
 */
export function getCurrentUser() {
  return auth.currentUser
}

/**
 * Listen to auth state changes
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback)
}
